import { GENERIC_SWAP_ABI, GS_EIP712_TYPES, MAX_UINT256, apiVersion, bpBase, feeReceiptAddress, genericSwapAddress, nullAddress, strategyAddress, usdtAddress, wrapperTokenAddress, } from '../constants/index.js';
import { ContextHost } from '../service/contextHost.js';
import { PermitSource } from '../types/permit2.js';
import { http } from '../utils/request/index.js';
import { httpServiceHost } from './../serviceHost.js';
import {} from './type.js';
import { JsonRpcProvider } from 'ethers';
import { decodeAbiParameters, encodeAbiParameters, encodeFunctionData, encodePacked, isAddressEqual, parseAbiItem, parseAbiParameters, zeroAddress, } from 'viem';
export const strategyOperationsABI = parseAbiParameters('(address dest, address inputToken, uint128 inputRatio, uint128 dataOffset, uint256 value, bytes data)[]');
export class Swap extends ContextHost {
    constructor(config, injected) {
        super({ env: config.env, chainId: config.chain }, {
            publicClient: injected.publicClient,
        });
        Object.defineProperty(this, "walletClient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "provider", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "price", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rebuildStrategyData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (fee, swapData, operations) => {
                const newOperations = [...operations];
                if (fee > 0n) {
                    const feeTransfer = encodeFunctionData({
                        abi: [parseAbiItem('function transfer(address to, uint256 amount)')],
                        functionName: 'transfer',
                        args: [feeReceiptAddress[this.chainId], fee],
                    });
                    if (!wrapperTokenAddress[this.chainId]) {
                        throw new Error('wrapper token address not found');
                    }
                    const feeToken = this.isNativeToken(swapData.makerToken)
                        ? wrapperTokenAddress[this.chainId]
                        : swapData.makerToken;
                    const feeOperation = {
                        dest: feeToken,
                        inputToken: feeToken,
                        inputRatio: 0n,
                        dataOffset: 0n,
                        value: 0n,
                        data: feeTransfer,
                    };
                    newOperations.push(feeOperation);
                }
                const newStrategyData = encodeAbiParameters(strategyOperationsABI, [
                    newOperations,
                ]);
                return newStrategyData;
            }
        });
        Object.defineProperty(this, "getProtocolFeeAmount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (order) => {
                const { swapData, feeFactor } = order;
                const { makerTokenAmount } = swapData;
                const protocolFeeAmount = (BigInt(makerTokenAmount) * BigInt(feeFactor)) / bpBase;
                return protocolFeeAmount;
            }
        });
        Object.defineProperty(this, "getSlippageAmount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (order, slippage) => {
                const { swapData } = order;
                const { makerTokenAmount } = swapData;
                const slippageAmount = (BigInt(makerTokenAmount) * BigInt(slippage)) / bpBase;
                return slippageAmount;
            }
        });
        Object.defineProperty(this, "calcGasFeeTokenAmount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ gasFeeAmount, feeToken, }) => {
                const gasFeeValue = await this.price.getTokenValue(zeroAddress, gasFeeAmount);
                const gasFeeToTokenAmount = await this.price.convertTokenValueToAmount({
                    value: gasFeeValue,
                    tokenAddress: feeToken,
                });
                return gasFeeToTokenAmount;
            }
        });
        Object.defineProperty(this, "calcRelayerFee", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ protocolFeeAmount, gasFeeTokenAmount, }) => {
                return gasFeeTokenAmount + protocolFeeAmount;
            }
        });
        Object.defineProperty(this, "getRoutesApproveStrategy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (takerTokenAmount, needApprovalDestinations) => {
                if (!Array.isArray(needApprovalDestinations) ||
                    needApprovalDestinations.length === 0) {
                    return [];
                }
                const allowances = await Promise.all([
                    ...needApprovalDestinations.map(({ dest, inputToken }) => this.publicClient.readContract({
                        abi: [
                            parseAbiItem('function allowance(address owner, address spender) view returns (uint256)'),
                        ],
                        functionName: 'allowance',
                        address: inputToken,
                        args: [strategyAddress[this.chainId], dest],
                    })),
                ]);
                const approveStrategies = allowances.reduce((acc, allowance, index) => {
                    try {
                        if (BigInt(allowance) < BigInt(takerTokenAmount)) {
                            const { dest, inputToken } = needApprovalDestinations[index];
                            const approve = encodeFunctionData({
                                abi: [
                                    parseAbiItem('function approve(address spender, uint256 amount) returns (bool)'),
                                ],
                                functionName: 'approve',
                                args: [dest, MAX_UINT256],
                            });
                            const approveOperation = {
                                dest: inputToken,
                                inputToken: inputToken,
                                inputRatio: 0n,
                                dataOffset: 0n,
                                value: 0n,
                                data: approve,
                            };
                            acc.push(approveOperation);
                            if (inputToken.toLowerCase() ===
                                usdtAddress[this.chainId]?.toLowerCase() &&
                                BigInt(allowance) > 0n) {
                                const approve2Zero = encodeFunctionData({
                                    abi: [
                                        parseAbiItem('function approve(address spender, uint256 amount) returns (bool)'),
                                    ],
                                    functionName: 'approve',
                                    args: [dest, 0n],
                                });
                                const approveOperation = {
                                    dest: inputToken,
                                    inputToken: inputToken,
                                    inputRatio: 0n,
                                    dataOffset: 0n,
                                    value: 0n,
                                    data: approve2Zero,
                                };
                                acc.unshift(approveOperation);
                            }
                            return acc;
                        }
                        return acc;
                    }
                    catch {
                        return acc;
                    }
                }, []);
                return approveStrategies;
            }
        });
        Object.defineProperty(this, "prepareOrder", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ order, slippage, gasFee = 0n, sendByRelayer, }) => {
                const { swapData, needApprovalDestinations } = order;
                const { recipient, strategyData, makerToken } = swapData;
                const makerTokenAmount = BigInt(swapData.makerTokenAmount);
                const slippageAmount = this.getSlippageAmount(order, slippage);
                const protocolFeeAmount = this.getProtocolFeeAmount(order);
                let fee = protocolFeeAmount;
                let gasFeeTokenAmount = gasFee;
                if (sendByRelayer) {
                    gasFeeTokenAmount = await this.calcGasFeeTokenAmount({
                        gasFeeAmount: gasFee,
                        feeToken: makerToken,
                    });
                    fee = await this.calcRelayerFee({
                        protocolFeeAmount,
                        gasFeeTokenAmount,
                    });
                }
                const minMakerTokenAmount = makerTokenAmount - slippageAmount - fee;
                swapData.minMakerTokenAmount = minMakerTokenAmount.toString();
                const [operations] = decodeAbiParameters(strategyOperationsABI, strategyData);
                const approveOperations = await this.getRoutesApproveStrategy(swapData.takerTokenAmount, needApprovalDestinations);
                const newStrategyData = this.rebuildStrategyData(fee, swapData, [
                    ...approveOperations,
                    ...operations,
                ]);
                const value = isAddressEqual(swapData.takerToken, nullAddress) ||
                    isAddressEqual(swapData.takerToken, zeroAddress)
                    ? BigInt(swapData.takerTokenAmount)
                    : 0n;
                const newSwapData = {
                    ...swapData,
                    strategyData: newStrategyData,
                    takerTokenAmount: BigInt(swapData.takerTokenAmount),
                    makerTokenAmount: BigInt(swapData.makerTokenAmount),
                    minMakerTokenAmount: BigInt(swapData.minMakerTokenAmount),
                    salt: BigInt(swapData.salt),
                    expiry: BigInt(swapData.expiry),
                };
                return {
                    swapData: newSwapData,
                    protocolFee: protocolFeeAmount,
                    gasFeeTokenAmount,
                    gasFee,
                    fee,
                    recipient,
                    strategyData: newStrategyData,
                    value,
                };
            }
        });
        Object.defineProperty(this, "getTokenList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (chainId) => {
                const _chainId = chainId ?? this.chainId;
                const tokenList = await http(`${this.host(httpServiceHost)}/${apiVersion}/swap/tokenlist/${_chainId}`);
                return tokenList;
            }
        });
        Object.defineProperty(this, "quote", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (dto) => {
                if (!dto.chainId)
                    dto.chainId = this.chainId;
                const { chainId, toChainId, ...rest } = dto;
                const isCrossSwap = toChainId && chainId !== toChainId;
                const chainIdPath = isCrossSwap ? `${chainId}/${toChainId}` : `${chainId}`;
                const p = new URLSearchParams();
                Object.entries(rest).forEach(([key, value]) => {
                    p.append(key, value.toString());
                });
                const order = await http(`${this.host(httpServiceHost)}/${apiVersion}/swap/quote/${chainIdPath}?${p.toString()}`);
                return order;
            }
        });
        Object.defineProperty(this, "getTokenPermitData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (token, permitType = PermitSource.Permit2AllowanceTransfer) => {
                const permitToAllowanceTarget = encodePacked(['uint8', 'bytes'], [permitType, '0x']);
                let takerTokenPermit = permitToAllowanceTarget;
                if (this.isNativeToken(token)) {
                    takerTokenPermit = '0x';
                }
                return takerTokenPermit;
            }
        });
        Object.defineProperty(this, "isNativeToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (tokenAddress) => {
                return (isAddressEqual(tokenAddress, nullAddress) ||
                    isAddressEqual(tokenAddress, zeroAddress));
            }
        });
        Object.defineProperty(this, "estimate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ account, order, slippage, permit, }) => {
                const { swapData, value } = await this.prepareOrder({
                    order,
                    slippage,
                });
                const takerTokenPermit = permit ?? this.getTokenPermitData(swapData.takerToken);
                const gasEstimate = await this.publicClient.estimateContractGas({
                    address: genericSwapAddress[this.chainId],
                    abi: GENERIC_SWAP_ABI,
                    functionName: 'executeSwap',
                    args: [swapData, takerTokenPermit],
                    value,
                    account,
                });
                return gasEstimate;
            }
        });
        Object.defineProperty(this, "sign", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                const { account, swapData, takerTokenPermit, value = 0n, isAcrossSwap, txOverride, } = params;
                const salt = swapData.salt.toString(16).padStart(64, '0');
                const tokenlonIdentifier = `4c4f4e3130${salt}`;
                const acrossIdentifier = '1dc0de004e';
                const dataSuffix = `0x${tokenlonIdentifier}${isAcrossSwap ? acrossIdentifier : ''}`;
                const txHash = await this.walletClient.writeContract({
                    address: genericSwapAddress[this.chainId],
                    abi: GENERIC_SWAP_ABI,
                    account,
                    functionName: 'executeSwap',
                    args: [swapData, takerTokenPermit],
                    value,
                    dataSuffix,
                    chain: this.walletClient.chain,
                    ...txOverride,
                });
                return txHash;
            }
        });
        Object.defineProperty(this, "signWithReceipt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (params) => {
                const txHash = await this.sign(params);
                const receipt = await this.publicClient.waitForTransactionReceipt({
                    hash: txHash,
                });
                return receipt;
            }
        });
        Object.defineProperty(this, "signTypedData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (swapData, account) => {
                const EIP712_DOMAIN = {
                    name: 'Tokenlon',
                    version: 'v6',
                    chainId: this.chainId,
                    verifyingContract: genericSwapAddress[this.chainId],
                };
                const EIP712_TYPED = {
                    domain: EIP712_DOMAIN,
                    types: GS_EIP712_TYPES,
                    primaryType: 'GenericSwapData',
                };
                return this.walletClient.signTypedData({
                    ...EIP712_TYPED,
                    message: swapData,
                    account,
                });
            }
        });
        Object.defineProperty(this, "submit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (dto) => {
                return http(`${this.host(httpServiceHost)}/${apiVersion}/swap/submit/${this.chainId}`, {
                    fetchOptions: {
                        method: 'POST',
                    },
                    body: dto,
                });
            }
        });
        Object.defineProperty(this, "list", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (dto) => {
                const params = new URLSearchParams({
                    userAddress: dto.account,
                });
                return http(`${this.host(httpServiceHost)}?${params}`);
            }
        });
        Object.defineProperty(this, "status", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (dto) => {
                const { toChainId, ...rest } = dto;
                const params = new URLSearchParams({
                    ...rest,
                });
                const chainIdPath = toChainId
                    ? `${this.chainId}/${toChainId}`
                    : `${this.chainId}`;
                return http(`${this.host(httpServiceHost)}/${apiVersion}/swap/status/${chainIdPath}?${params}`);
            }
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (appVersion) => {
                return http(`${this.host(httpServiceHost)}/${apiVersion}/swap/config?appVersion=${appVersion}`);
            }
        });
        Object.defineProperty(this, "ordersHistory", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ chainId, userAddress, limit, offset, }) => {
                const params = new URLSearchParams({
                    userAddress,
                    limit: limit.toString(),
                    offset: offset.toString(),
                });
                return http(`${this.host(httpServiceHost)}/${apiVersion}/swap/orders/${chainId}?${params}`);
            }
        });
        Object.defineProperty(this, "trendingList", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                return http(`${this.host(httpServiceHost)}/${apiVersion}/swap/trendinglist`);
            }
        });
        Object.defineProperty(this, "tradingAmountLimits", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ fromChainId, toChainId, fromToken, toToken, }) => {
                const params = new URLSearchParams({
                    fromToken,
                    toToken,
                });
                return http(`${this.host(httpServiceHost)}/${apiVersion}/swap/limits/${fromChainId}/${toChainId}?${params}`);
            }
        });
        this.walletClient = injected.walletClient;
        this.provider = injected.provider;
        this.price = injected.price;
    }
}
//# sourceMappingURL=index.js.map